import { useMemo, forwardRef, useRef } from 'react';
import { nanoid } from 'nanoid';
import cn from 'classnames';

import { Svg } from 'components/Svg';
import s from './s.module.css';

import DTP from 'react-datetime-picker';

export function DateTimePicker({ className, value, valid, ...rest }) {
  return (
    <div
      className={cn(
        s.label,
        { [s.hasValue]: !!value, [s.customValidation]: valid !== undefined },
        className
      )}
    >
      <DTP
        className={cn(s.datetimepicker, { [s.valid]: valid })}
        value={value}
        {...rest}
      />
      <div className={s.validation}>
        <Svg id="checkmark" />
      </div>
    </div>
  );
}

export function Input({ children, id, className, value, valid, ...rest }) {
  const inputId = useMemo(() => id ?? nanoid(), [id]);

  return (
    <label
      className={cn(
        s.label,
        { [s.hasValue]: !!value, [s.customValidation]: valid !== undefined },
        className
      )}
      htmlFor={inputId}
    >
      <input
        className={cn(s.input, { [s.hasValue]: !!value, [s.valid]: valid })}
        id={inputId}
        value={value}
        {...rest}
      />
      <div className={cn(s.text, { [s.hasValue]: !!value })}>{children}</div>
      <div className={s.validation}>
        <Svg id="checkmark" />
      </div>
    </label>
  );
}

export const Select = forwardRef(
  ({ children, options, id, className, value, ...rest }, ref) => {
    const inputId = useMemo(() => id ?? nanoid(), [id]);

    const selectedOption = useMemo(
      () => value && options.find((opt) => opt.value === value),
      [value, options]
    );

    return (
      <label
        ref={ref}
        className={cn(
          s.selectLabel,
          { [s.hasValue]: !!selectedOption, [s.disabled]: rest.disabled },
          className
        )}
        htmlFor={inputId}
      >
        <div className={cn(s.text, { [s.hasValue]: !!selectedOption })}>
          {children}
        </div>
        <select className={s.select} id={inputId} value={value} {...rest}>
          {options.map((opt) => (
            <option {...opt} key={opt.value} />
          ))}
        </select>
        {selectedOption && (
          <div className={s.data}>{selectedOption.children}</div>
        )}
      </label>
    );
  }
);

export function Textarea({ children, id, className, value, valid, ...rest }) {
  const inputId = useMemo(() => id ?? nanoid(), [id]);

  return (
    <label
      className={cn(
        s.label,
        { [s.hasValue]: !!value, [s.customValidation]: valid !== undefined },
        className
      )}
      htmlFor={inputId}
    >
      <textarea
        className={cn(s.textarea, { [s.hasValue]: !!value, [s.valid]: valid })}
        id={inputId}
        value={value}
        {...rest}
      />
      <div className={cn(s.text, s.textareaText, { [s.hasValue]: !!value })}>
        {children}
      </div>
      <div className={s.validation}>
        <Svg id="checkmark" />
      </div>
    </label>
  );
}

export function Checkbox({
  id,
  className,
  checked,
  onChange,
  children,
  ...rest
}) {
  const inputId = useMemo(() => id ?? nanoid(), [id]);

  const handlerKeyPress = (event) => {
    if (event.key === 'Enter') {
      onChange(event);
    }
  };

  return (
    <label
      className={cn(s.checkboxWrap, className)}
      tabIndex={0}
      role="checkbox"
      aria-checked={checked}
      onKeyPress={handlerKeyPress}
      htmlFor={inputId}
    >
      <input
        type="checkbox"
        className={s.checkboxInput}
        id={inputId}
        checked={checked}
        onChange={onChange}
        tabIndex={-1}
        {...rest}
      />
      <span className={s.checkboxPlaceholder}>
        <Svg id={checked ? 'checkbox-on' : 'checkbox-off'} />
      </span>
      {children}
    </label>
  );
}

export function SignInput({ children, id, className, value, ...rest }) {
  const inputId = useMemo(() => id ?? nanoid(), [id]);
  const textareaRef = useRef();

  const copyToClipboard = () => {
    textareaRef.current.select();
    document.execCommand('copy');
  };

  return (
    <label className={cn(s.signLabel, className)} htmlFor={inputId}>
      <div className={s.signText}>Lotus sign command</div>
      <textarea
        className={s.textarea}
        id={inputId}
        value={value}
        ref={textareaRef}
        {...rest}
      />
      <button type="button" onClick={copyToClipboard} className={s.copyButton}>
        <Svg id="copy" />
      </button>
    </label>
  );
}
